import { computed } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { FitToWindowButton } from "../Buttons/FitToWindowButton";
import { OriginalSizeButton } from "../Buttons/OriginalSizeButton";
import { ZoomInButton } from "../Buttons/Pdf/ZoomInButton";
import { ZoomOutButton } from "../Buttons/Pdf/ZoomOutButton";
import { Area } from "../General/Area";
import { AreaBody } from "../General/AreaBody";
import { FullScreen } from "../General/FullScreen";
import { Toolbar } from "../General/Toolbar";
import { Strings } from "../Stores/StringsStore";
import { AspectRatio, ImageZoomerImpl } from "./ImageZoomer";
import { ReaderToolbarMode, ReaderToolbarPart } from "./ReaderToolbarPart";

interface IImageViewProps {
	id: string;
	ext: string;
	docId: string;
	docLanguage: string;
	collectionId: string;
	downloadName: string;
	canDownload: boolean;
	fileName: string;
	originalFilename: string;
	isDeleted: boolean;
}

@observer
export class ImageView extends React.Component<IImageViewProps, {}> {

	ImageZoomer: ImageZoomerImpl = new ImageZoomerImpl();

	@computed get source(): string {
		let id = this.props.id;
		let ext = this.props.ext;
		let path = encodeURI(`/files/${id}${ext}`);
		return path;
	}

	componentDidMount() {
		window.addEventListener('resize', this.ImageZoomer.windowResizing);
		window.addEventListener('orientationchange', this.ImageZoomer.resetImage);
		window.addEventListener("mouseup", this.mouseup);
		window.addEventListener("mousemove", this.mousemove);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.ImageZoomer.windowResizing);
		window.removeEventListener('orientationchange', this.ImageZoomer.resetImage);
		window.removeEventListener("mouseup", this.mouseup);
		window.removeEventListener("mousemove", this.mousemove);
	}

	mouseup = () => {
		this.ImageZoomer.onMouseUp.bind(this);
	}

	mousemove = () => {
		this.ImageZoomer.onMouseMove.bind(this);
	}

	preventContextMenu(event: React.MouseEvent<HTMLImageElement>) {
		event.preventDefault();
		return false;
	}

	render() {
		let spinner = this.ImageZoomer.image.isLoading ? <div className="spinner">
			<div className="spinner-inner">
				<i>
					<svg className="spinner-animation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
						<path className="spinner-custom" d="M24,5A19,19,0,0,1,43,24h3A22,22,0,0,0,24,2Z" />
						<path d="M24,43A19,19,0,0,1,24,5V2A22,22,0,1,0,46,24H43A19,19,0,0,1,24,43Z" />
					</svg>
				</i>
			</div>
			<div className="spinner-label">{this.ImageZoomer.image.isLoading ? Strings.reader.imageReszing : Strings.general.loading}</div>
		</div> : null;

		let dropDownValues = ["100%", "150%", "200%", "300%", "400%"];
		let dropDownValue = this.ImageZoomer.image.zoomScale * 100 + "%";

		return <Area>
			<AreaBody>
				<FullScreen>
					<div className="imgViewer">
						<Toolbar>
							<span className="buttongroup is--buttongroup-left">
								<ZoomOutButton testId="zoomOutButton" onClick={this.ImageZoomer.zoomButtonClick.bind(this, false)} disabled={!this.ImageZoomer.canZoomOut} />
								<ZoomInButton testId="zoomInButton" onClick={this.ImageZoomer.zoomButtonClick.bind(this, true)} disabled={!this.ImageZoomer.canZoomIn} />
								<select id="zoomSelect" value={dropDownValue} onChange={this.ImageZoomer.onDropDownChange.bind(this)}>
									{dropDownValues.indexOf(dropDownValue) === -1 ? <option value={dropDownValue} disabled style={{ display: "none" }}>{dropDownValue}</option> : null}
									{dropDownValues.map((value, index) => { return <option key={index} value={value}>{value}</option> })}
								</select>
								<FitToWindowButton onClick={this.ImageZoomer.handleAspectRatio.bind(this, AspectRatio.Fit)} title={Strings.reader.fitToWindowButtonTooltip} disabled={!this.ImageZoomer.image.isOriginalSize} />
								<OriginalSizeButton onClick={this.ImageZoomer.handleAspectRatio.bind(this, AspectRatio.Original)} title={Strings.reader.originalSizeButtonTooltip} disabled={this.ImageZoomer.image.isOriginalSize} />
							</span>
							<ReaderToolbarPart
								id={this.props.id}
								docId={this.props.docId}
								docLanguage={this.props.docLanguage}
								collectionId={this.props.collectionId}
								downloadName={this.props.downloadName}
								canDownload={this.props.canDownload}
								mode={ReaderToolbarMode.Image}
								extension={this.props.ext}
								rotateClockwiseDisabled={this.ImageZoomer.image.isLoading}
								onRotateClockwise={this.ImageZoomer.rotateClockwise}
								rotateCounterClockwiseDisabled={this.ImageZoomer.image.isLoading}
								fileName={this.props.fileName}
								onRotateCounterClockwise={this.ImageZoomer.rotateCounterClockwise}
								isDeleted={this.props.isDeleted}
								originalFilename={this.props.originalFilename}
							/>
						</Toolbar>
						{spinner}
						<div id={this.ImageZoomer.IMG_CONTAINER_ID} className="imageOverlay"
							onTouchStart={this.ImageZoomer.imgOverlayTouch.bind(this)}
							onTouchMove={this.ImageZoomer.imgOverlayTouch.bind(this)}
							onTouchEnd={this.ImageZoomer.imgOverlayTouch.bind(this)}>
							<img id={this.ImageZoomer.IMG_ID} onLoad={this.ImageZoomer.onImgLoad} onError={() => { this.ImageZoomer.image.isLoading = false; }} src={this.source} data-rotate={this.ImageZoomer.image.rotation} style={{
								position: 'relative',
								left: this.ImageZoomer.image.imageLeft + "px",
								top: this.ImageZoomer.image.imageTop + "px",
								maxHeight: !this.ImageZoomer.image.isLoading && this.ImageZoomer.image.mode !== AspectRatio.Original ? this.ImageZoomer.image.maxHeight + "px" : "none",
								maxWidth: !this.ImageZoomer.image.isLoading && this.ImageZoomer.image.mode !== AspectRatio.Original ? this.ImageZoomer.image.maxWidth + "px" : "none",
								minHeight: this.ImageZoomer.image.minHeight,
								minWidth: this.ImageZoomer.image.minWidth,
								marginTop: this.ImageZoomer.image.marginTop + "px",
								marginLeft: !this.ImageZoomer.image.marginLeft ? "auto" : this.ImageZoomer.image.marginLeft + "px"
							}}
								onMouseDown={this.ImageZoomer.onMouseDown.bind(this)}
								onMouseUp={this.ImageZoomer.onMouseUp.bind(this)}
								onWheel={this.ImageZoomer.handleScroll.bind(this)}
								onTouchStart={this.ImageZoomer.handleTouch.bind(this)}
								onTouchMove={this.ImageZoomer.handleTouch.bind(this)}
								onTouchEnd={this.ImageZoomer.handleTouch.bind(this)}
								onContextMenu={this.preventContextMenu} />
						</div>
					</div>
				</FullScreen>
			</AreaBody>
		</Area>;
	}

}